package com.example.monetix.utils

import com.example.monetix.db.RecurringExpenseEntity
import kotlinx.datetime.LocalDate
import kotlinx.datetime.DayOfWeek

object PaymentEngine {

    private fun calculateNextDateEpoch(item: RecurringExpenseEntity): Long {
        val currentDate = LocalDate.fromEpochDays(item.nextEpochDay.toInt())

        // 🚀 Supporto per le frequenze personalizzate
        if (item.frequency?.startsWith("CUSTOM_") == true) {
            val parts = item.frequency.split("_")
            val daysToAdd = parts.getOrNull(1)?.toIntOrNull() ?: 30
            val skipWeekend = parts.getOrNull(2) == "SKIP"

            var nextEpoch = item.nextEpochDay + daysToAdd

            if (skipWeekend) {
                val dateObj = LocalDate.fromEpochDays(nextEpoch.toInt())
                val dayOfWeek = dateObj.dayOfWeek
                if (dayOfWeek == DayOfWeek.SATURDAY) nextEpoch += 2 // Da Sabato slitta a Lunedì
                else if (dayOfWeek == DayOfWeek.SUNDAY) nextEpoch += 1 // Da Domenica slitta a Lunedì
            }
            return nextEpoch
        } else {
            // Logica Mensile classica
            var nextM = currentDate.monthNumber + 1
            var nextY = currentDate.year
            if (nextM > 12) { nextM = 1; nextY++ }
            val targetDay = item.dayOfMonth.toInt()
            val maxD = when (nextM) { 2 -> if (nextY % 4 == 0 && (nextY % 100 != 0 || nextY % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
            return LocalDate(nextY, nextM, targetDay.coerceAtMost(maxD)).toEpochDays().toLong()
        }
    }

    private fun calculatePreviousDateEpoch(item: RecurringExpenseEntity): Long {
        val currentDate = LocalDate.fromEpochDays(item.nextEpochDay.toInt())

        if (item.frequency?.startsWith("CUSTOM_") == true) {
            val parts = item.frequency.split("_")
            val daysToSub = parts.getOrNull(1)?.toIntOrNull() ?: 30
            return item.nextEpochDay - daysToSub
        } else {
            var prevM = currentDate.monthNumber - 1
            var prevY = currentDate.year
            if (prevM < 1) { prevM = 12; prevY-- }
            val targetDay = item.dayOfMonth.toInt()
            val maxD = when (prevM) { 2 -> if (prevY % 4 == 0 && (prevY % 100 != 0 || prevY % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
            return LocalDate(prevY, prevM, targetDay.coerceAtMost(maxD)).toEpochDays().toLong()
        }
    }

    fun processPaymentState(item: RecurringExpenseEntity, nowMs: Long): RecurringExpenseEntity {
        val newEpoch = calculateNextDateEpoch(item)
        if (item.notes?.contains("[CART:") != true) return item.copy(nextEpochDay = newEpoch, updatedAtEpochMs = nowMs)

        val notes = item.notes ?: ""
        val cartellaRegex = Regex("\\[CART:([^\\]]*)\\]")
        val match = cartellaRegex.find(notes) ?: return item.copy(nextEpochDay = newEpoch, updatedAtEpochMs = nowMs)

        val ratesStr = match.groupValues[1]
        val rates = if (ratesStr.isNotBlank()) ratesStr.split(",").map { it.trim() }.toMutableList() else mutableListOf()

        var nextAmount = item.amountCents
        var isFinished = true

        for (i in rates.indices) {
            if (!rates[i].endsWith(":P", ignoreCase = true) && !rates[i].endsWith(":X", ignoreCase = true)) {
                rates[i] = rates[i] + ":P"

                val nextIndex = i + 1
                if (nextIndex < rates.size) {
                    nextAmount = rates[nextIndex].replace(":P", "").toLongOrNull() ?: 0L
                    isFinished = false
                } else {
                    nextAmount = 0L
                    isFinished = true
                }
                break
            }
        }

        val newCartList = rates.joinToString(",")
        val willBeArchived = isFinished || notes.contains("[ARCHIVED:true]")

        var newNotes = notes.replace(match.value, "[CART:$newCartList]")
        if (willBeArchived && !newNotes.contains("[ARCHIVED:true]")) newNotes += " [ARCHIVED:true]"

        return item.copy(amountCents = nextAmount, notes = newNotes.trim(), nextEpochDay = newEpoch, isEnabled = !willBeArchived && item.isEnabled, updatedAtEpochMs = nowMs)
    }

    fun processUndoState(item: RecurringExpenseEntity, revertedAmountCents: Long, nowMs: Long): RecurringExpenseEntity {
        val newEpoch = calculatePreviousDateEpoch(item)
        if (item.notes?.contains("[CART:") != true) return item.copy(nextEpochDay = newEpoch, updatedAtEpochMs = nowMs)

        val notes = item.notes ?: ""
        val cartellaRegex = Regex("\\[CART:([^\\]]*)\\]")
        val match = cartellaRegex.find(notes) ?: return item.copy(nextEpochDay = newEpoch, updatedAtEpochMs = nowMs)

        val rates = match.groupValues[1].split(",").map { it.trim() }.toMutableList()
        var restoredAmount = item.amountCents

        for (i in rates.indices.reversed()) {
            if (rates[i].endsWith(":P")) {
                rates[i] = rates[i].replace(":P", "")
                restoredAmount = rates[i].toLongOrNull() ?: revertedAmountCents
                break
            }
        }

        val newCartList = rates.joinToString(",")
        var newNotes = notes.replace(match.value, "[CART:$newCartList]")
        newNotes = newNotes.replace(" [ARCHIVED:true]", "").replace("[ARCHIVED:true]", "").trim()

        return item.copy(amountCents = restoredAmount, notes = newNotes.trim(), nextEpochDay = newEpoch, isEnabled = true, updatedAtEpochMs = nowMs)
    }
}